import React, { useEffect } from "react";
import PropTypes from "prop-types";

import { getUserName } from "utils/helpers/users";
import { useQuery } from "@apollo/client";
import { GET_USER_BY_REFERRAL_CODE } from "api/graphql/user";

// Shows a placeholder while the lookup runs, then the referrer's name and code.
// A code with no user shows on its own; a failed lookup is logged and marked as
// failed, so it can't be mistaken for an unknown code. Errors are returned, not
// thrown, so a failed lookup never crashes the card.
const Referrer = ({ referralCode }) => {
  const { data, loading, error } = useQuery(GET_USER_BY_REFERRAL_CODE, {
    variables: { referralCode },
    skip: !referralCode,
  });

  useEffect(() => {
    if (error) {
      console.error(`Referrer lookup failed for code ${referralCode}:`, error);
    }
  }, [error, referralCode]);

  if (!referralCode) return null;

  const referrerName = getUserName(data?.getUserByReferralCode?.items?.[0]);

  let referrer;
  if (loading) {
    referrer = (
      // Same look as components/ui/skeleton, which renders a <div> and can't sit in this <span>
      <span
        role="status"
        aria-label="Loading referrer"
        className="inline-block h-4 w-32 animate-pulse rounded-md bg-muted align-middle"
      />
    );
  } else if (error) {
    referrer = (
      <>
        {referralCode}{" "}
        <span className="text-muted-foreground">(referrer lookup failed)</span>
      </>
    );
  } else {
    referrer = referrerName
      ? `${referrerName} (${referralCode})`
      : referralCode;
  }

  return <span className="min-w-0 break-words">Referred by: {referrer}</span>;
};

Referrer.propTypes = {
  referralCode: PropTypes.string,
};

Referrer.defaultProps = {
  referralCode: "",
};

export default Referrer;
